import { jsonApiRequest, apiRequest, readExportWarnings } from "../api";

// ==================== SCHEMA DEFINITIONS ====================
// Schema definitions are structural templates that define expected columns
// and structural fields for file formats (Shopify, WooCommerce, etc.)

export async function getSchemaDefinitions() {
  const response = await jsonApiRequest("GET", `schema-definitions`);
  return response;
}

export async function getSchemaDefinitionById(id) {
  const response = await jsonApiRequest("GET", `schema-definitions/${id}`);
  return response;
}

export async function createSchemaDefinition(schemaDefinition) {
  const response = await jsonApiRequest("POST", `schema-definitions`, schemaDefinition);
  return response;
}

export async function updateSchemaDefinition(id, schemaDefinition) {
  const response = await jsonApiRequest("PUT", `schema-definitions/${id}`, schemaDefinition);
  return response;
}

export async function deleteSchemaDefinition(id) {
  const response = await jsonApiRequest("DELETE", `schema-definitions/${id}`);
  return response;
}

// ==================== SCHEMAS ====================

export async function getPimSchemas(tenantId) {
  const response = await jsonApiRequest("GET", `pim/schemas?tenantId=${tenantId}`);
  return response;
}

export async function getPimStandardSchemas() {
  return await jsonApiRequest("GET", "pim/schemas/standards");
}

export async function getPimImportSchemas(tenantId) {
  return await jsonApiRequest("GET", `pim/schemas?tenantId=${tenantId}&kind=Import`);
}

// The well-known platform formats FacetFlux ships, ready to install as mapping targets.
export async function getSchemaLibrary() {
  return await jsonApiRequest("GET", "pim/schemas/library");
}

// mode: "asTarget" (a Recipient schema you map to) or "asMaster" (start your catalogue from it).
export async function installLibrarySchema(key, mode = "asTarget", name = null) {
  return await jsonApiRequest("POST", `pim/schemas/library/${key}/install`, { mode, name });
}

export async function getPimSchemaById(id) {
  const response = await jsonApiRequest("GET", `pim/schemas/${id}`);
  return response;
}

export async function getPimSchemaByName(tenantId, name) {
  const response = await jsonApiRequest("GET", `pim/schemas/by-name?tenantId=${tenantId}&name=${encodeURIComponent(name)}`);
  return response;
}

export async function createPimSchema(schema) {
  const response = await jsonApiRequest("POST", `pim/schemas`, schema);
  return response;
}

export async function updatePimSchema(id, schema) {
  const response = await jsonApiRequest("PUT", `pim/schemas/${id}`, schema);
  return response;
}

export async function deletePimSchema(id) {
  const response = await jsonApiRequest("DELETE", `pim/schemas/${id}`);
  return response;
}

// Schema export/import (GlobalAdmin only).
// Cross-references in the export use natural keys (Code) so it can be applied
// to any tenant. See SchemaPortabilityService on the backend.
export async function exportPimSchema(schemaId, tenantId) {
  return await jsonApiRequest("GET", `pim/schemas/${schemaId}/export?tenantId=${tenantId}`);
}

export async function importPimSchema(exportData, targetTenantId, replaceExisting = false) {
  return await jsonApiRequest("POST", `pim/schemas/import`, {
    export: exportData,
    targetTenantId,
    replaceExisting,
  });
}

// Product data export/import. Available to all authenticated tenant users.
// Bundle includes products grouped by schema, variants, attribute values, and
// asset metadata. The target tenant must already have schemas matching the
// bundle's schemaName + schemaKind groups — import the schema first if needed.
export async function exportPimProducts(schemaId) {
  return await jsonApiRequest("GET", `pim/products/export?schemaId=${schemaId}`);
}

export async function exportPimProductsBySelection(productIds) {
  return await jsonApiRequest("POST", `pim/products/export-bundle`, { productIds });
}

export async function exportPimProductsByFilter(filter) {
  return await jsonApiRequest("POST", `pim/products/export-bundle`, { filter });
}

export async function importPimProducts(exportData, replaceExisting = false) {
  return await jsonApiRequest("POST", `pim/products/import`, {
    export: exportData,
    replaceExisting,
  });
}

export async function inferSchemaFromEsska() {
  const response = await jsonApiRequest("POST", `esska/import/infer-schema`);
  return response;
}

export async function inferSchemaFromImport(importId, schemaName = null) {
  const body = { importId };
  if (schemaName) {
    body.schemaName = schemaName;
  }
  const response = await jsonApiRequest("POST", `pim/schemas/infer`, body);
  return response;
}

export async function ensureCoreFields(schemaId) {
  return await jsonApiRequest("POST", `pim/schemas/${schemaId}/ensure-core-fields`);
}

// Document the schema's still-undocumented attributes with AI (description + guidance + examples).
// Pass traitId to scope to a single trait (e.g. the core fields); omit for the whole schema.
export async function documentSchemaWithAi(schemaId, traitId = null) {
  const q = traitId ? `?traitId=${encodeURIComponent(traitId)}` : "";
  return await jsonApiRequest("POST", `pim/schemas/${schemaId}/ai-documentation${q}`);
}

// Read-only: ask AI to propose product types + traits/axes for a set of (uncategorized) products.
// Writes nothing — a preview of how unknown imports could be organized.
export async function proposeStructure(productIds) {
  return await jsonApiRequest("POST", "pim/products/propose-structure", {
    productIds,
  });
}

export async function rebuildSchemaProjections(schemaId) {
  const response = await jsonApiRequest("POST", `pim/schemas/${schemaId}/rebuild-projections`);
  return response;
}

export async function getSchemaIntegrityAudit(schemaId) {
  return await jsonApiRequest("GET", `pim/schemas/${schemaId}/integrity-audit`);
}

// ==================== SCHEMA CLEANUP (SC-1 / SC-2) ====================

export async function getUnusedDefinitions(schemaId) {
  return await jsonApiRequest("GET", `pim/data-quality/unused-definitions?schemaId=${schemaId}`);
}

export async function pruneSchemaDefinitions(schemaId, definitionIds) {
  return await jsonApiRequest("POST", `pim/data-quality/prune`, { schemaId, definitionIds });
}

export async function getAxisCandidates(schemaId) {
  return await jsonApiRequest("GET", `pim/data-quality/axis-candidates?schemaId=${schemaId}`);
}

// ==================== ATTRIBUTE DEFINITIONS ====================

export async function getPimAttributeDefinitions(schemaId) {
  const response = await jsonApiRequest("GET", `pim/attribute-definitions?schemaId=${schemaId}`);
  return response;
}

export async function getPimAttributeDefinitionsPaginated(schemaId, traitId = null, search = null, page = 1, pageSize = 100) {
  let url = `pim/attribute-definitions/paginated?schemaId=${schemaId}&page=${page}&pageSize=${pageSize}`;
  if (traitId) url += `&traitId=${traitId}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  const response = await jsonApiRequest("GET", url);
  return response;
}

export async function getPimAttributeDefinitionsByType(schemaId, type) {
  const response = await jsonApiRequest("GET", `pim/attribute-definitions/by-type?schemaId=${schemaId}&type=${type}`);
  return response;
}

export async function getPimVariantDefiningAttributeDefinitions(schemaId) {
  const response = await jsonApiRequest("GET", `pim/attribute-definitions/variant-defining?schemaId=${schemaId}`);
  return response;
}

export async function getPimVariantScopedAttributeDefinitions(schemaId) {
  const response = await jsonApiRequest("GET", `pim/attribute-definitions/variant-scoped?schemaId=${schemaId}`);
  return response;
}

export async function getPimRequiredAttributeDefinitions(schemaId) {
  const response = await jsonApiRequest("GET", `pim/attribute-definitions/required?schemaId=${schemaId}`);
  return response;
}

export async function getPimAttributeDefinitionById(id) {
  const response = await jsonApiRequest("GET", `pim/attribute-definitions/${id}`);
  return response;
}

export async function getPimAttributeDefinitionByCode(schemaId, code) {
  const response = await jsonApiRequest("GET", `pim/attribute-definitions/by-code?schemaId=${schemaId}&code=${encodeURIComponent(code)}`);
  return response;
}

export async function createPimAttributeDefinition(attribute) {
  const response = await jsonApiRequest("POST", `pim/attribute-definitions`, attribute);
  return response;
}

export async function updatePimAttributeDefinition(id, attribute) {
  const response = await jsonApiRequest("PUT", `pim/attribute-definitions/${id}`, attribute);
  return response;
}

export async function deletePimAttributeDefinition(id) {
  const response = await jsonApiRequest("DELETE", `pim/attribute-definitions/${id}`);
  return response;
}

// ==================== ALLOWED VALUES ====================

export async function getPimAllowedValues(attributeId, page = 1, pageSize = 100, activeOnly = false) {
  const response = await jsonApiRequest("GET", `pim/attribute-definitions/${attributeId}/allowed-values?page=${page}&pageSize=${pageSize}&activeOnly=${activeOnly}`);
  return response;
}

export async function createPimAllowedValue(attributeId, allowedValue) {
  const response = await jsonApiRequest("POST", `pim/attribute-definitions/${attributeId}/allowed-values`, allowedValue);
  return response;
}

export async function updatePimAllowedValue(attributeId, valueId, allowedValue) {
  const response = await jsonApiRequest("PUT", `pim/attribute-definitions/${attributeId}/allowed-values/${valueId}`, allowedValue);
  return response;
}

export async function deletePimAllowedValue(attributeId, valueId) {
  const response = await jsonApiRequest("DELETE", `pim/attribute-definitions/${attributeId}/allowed-values/${valueId}`);
  return response;
}

// Resolve a single display label from a {en,de,...} labels dict (or a DTO that already carries a resolved
// `label`), falling back so a de-only reflected schema never renders as its bare code:
// label -> labels.en -> labels.de -> first non-empty -> code.
export function displayLabel(obj) {
  if (!obj) return "";
  if (obj.label) return obj.label;
  const labels = obj.labels;
  if (labels) {
    if (labels.en) return labels.en;
    if (labels.de) return labels.de;
    const first = Object.values(labels).find((v) => v);
    if (first) return first;
  }
  return obj.code || "";
}

// ==================== TRAITS ====================

export async function getPimTraits(schemaId) {
  const response = await jsonApiRequest("GET", `pim/traits?schemaId=${schemaId}`);
  return response;
}

export async function getPimTraitsPaginated(schemaId, { search, page = 1, pageSize = 50 } = {}) {
  let url = `pim/traits/paginated?schemaId=${schemaId}&page=${page}&pageSize=${pageSize}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  return await jsonApiRequest("GET", url);
}

export async function getPimTraitById(id) {
  const response = await jsonApiRequest("GET", `pim/traits/${id}`);
  return response;
}

export async function createPimTrait(trait) {
  const response = await jsonApiRequest("POST", `pim/traits`, trait);
  return response;
}

export async function updatePimTrait(id, trait) {
  const response = await jsonApiRequest("PUT", `pim/traits/${id}`, trait);
  return response;
}

export async function deletePimTrait(id) {
  const response = await jsonApiRequest("DELETE", `pim/traits/${id}`);
  return response;
}

// ==================== PRODUCT TYPES ====================
// Product types define which traits/attributes apply. NOT sales categories.

export async function getPimProductTypes(schemaId) {
  return await jsonApiRequest("GET", `pim/product-types?schemaId=${schemaId}`);
}

export async function getPimProductTypeDetails(id) {
  return await jsonApiRequest("GET", `pim/product-types/${id}/details`);
}

// Assign (or clear, with null) a product's primary type. Adopts the type's default traits
// without dropping existing ones, and clears the "uncategorized" attention flag.
export async function assignProductType(productId, primaryTypeId) {
  return await jsonApiRequest("PUT", `pim/products/${productId}/type`, { primaryTypeId });
}

export async function getPimProductTypesPaginated(schemaId, { parentCode, search, page = 1, pageSize = 50 } = {}) {
  let url = `pim/product-types/paginated?schemaId=${schemaId}&page=${page}&pageSize=${pageSize}`;
  if (parentCode != null) url += `&parentCode=${encodeURIComponent(parentCode)}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  return await jsonApiRequest("GET", url);
}

// Product types that USE a given trait (DefaultTraitIds contains traitId), paginated.
export async function getPimProductTypesUsingTrait(schemaId, traitId, { page = 1, pageSize = 50 } = {}) {
  return await jsonApiRequest(
    "GET",
    `pim/product-types/using-trait?schemaId=${schemaId}&traitId=${traitId}&page=${page}&pageSize=${pageSize}`
  );
}

// Products of a product type (by PrimaryTypeId), paginated.
export async function getPimProductsByType(typeId, { page = 1, pageSize = 50 } = {}) {
  return await jsonApiRequest("GET", `pim/products/by-type/${typeId}?page=${page}&pageSize=${pageSize}`);
}

export async function getPimProductTypeById(id) {
  return await jsonApiRequest("GET", `pim/product-types/${id}`);
}

export async function createPimProductType(productType) {
  return await jsonApiRequest("POST", `pim/product-types`, productType);
}

export async function updatePimProductType(id, productType) {
  return await jsonApiRequest("PUT", `pim/product-types/${id}`, productType);
}

export async function deletePimProductType(id) {
  return await jsonApiRequest("DELETE", `pim/product-types/${id}`);
}

// ==================== SALES CATEGORIES ====================
// Categories are for sales/merchandising only (e.g. "Summer Sale", "New Arrivals").
// NOT for schema structure — use Product Types for that.

export async function getPimCategories(schemaId) {
  const response = await jsonApiRequest("GET", `pim/categories?schemaId=${schemaId}`);
  return response;
}

export async function getPimCategoriesTree(schemaId) {
  const response = await jsonApiRequest("GET", `pim/categories/tree?schemaId=${schemaId}`);
  return response;
}

export async function getPimCategoryById(id) {
  const response = await jsonApiRequest("GET", `pim/categories/${id}`);
  return response;
}

export async function createPimCategory(category) {
  const response = await jsonApiRequest("POST", `pim/categories`, category);
  return response;
}

export async function updatePimCategory(id, category) {
  const response = await jsonApiRequest("PUT", `pim/categories/${id}`, category);
  return response;
}

export async function movePimCategory(id, newParentId) {
  const response = await jsonApiRequest("POST", `pim/categories/${id}/move`, { newParentId });
  return response;
}

export async function deletePimCategory(id) {
  const response = await jsonApiRequest("DELETE", `pim/categories/${id}`);
  return response;
}

// ==================== DATA QUALITY: AI ANALYSIS ====================

export async function getAiAnalysis(schemaId) {
  return await jsonApiRequest("GET", `pim/data-quality/ai-analysis?schemaId=${schemaId}`);
}

export async function getAiAnalysisEstimate(schemaId) {
  return await jsonApiRequest("GET", `pim/data-quality/ai-analysis/estimate?schemaId=${schemaId}`);
}

export async function runAiAnalysis(schemaId) {
  return await jsonApiRequest("POST", `pim/data-quality/ai-analysis?schemaId=${schemaId}`);
}

// ==================== DATA QUALITY ====================

export async function getValueDistribution(attributeDefinitionId, schemaId = null, limit = 500) {
  let url = `pim/data-quality/value-distribution?attributeDefinitionId=${attributeDefinitionId}&limit=${limit}`;
  if (schemaId) url += `&schemaId=${schemaId}`;
  return await jsonApiRequest("GET", url);
}

export async function getCompletenessReport(schemaId, categoryId = null) {
  let url = `pim/data-quality/completeness?schemaId=${schemaId}`;
  if (categoryId) url += `&categoryId=${categoryId}`;
  return await jsonApiRequest("GET", url);
}

export async function getAttributeGaps(schemaId, attributeDefinitionId, categoryId = null, page = 1, pageSize = 50) {
  let url = `pim/data-quality/gaps?schemaId=${schemaId}&attributeDefinitionId=${attributeDefinitionId}&page=${page}&pageSize=${pageSize}`;
  if (categoryId) url += `&categoryId=${categoryId}`;
  return await jsonApiRequest("GET", url);
}

export async function getCategoryComparison(schemaId, parentCategoryId) {
  return await jsonApiRequest("GET", `pim/data-quality/category-comparison?schemaId=${schemaId}&parentCategoryId=${parentCategoryId}`);
}

export async function getDuplicates(schemaId, attributeDefinitionIds, limit = 100) {
  return await jsonApiRequest("GET", `pim/data-quality/duplicates?schemaId=${schemaId}&attributeDefinitionIds=${attributeDefinitionIds.join(",")}&limit=${limit}`);
}

export async function getOutliers(attributeDefinitionId, limit = 100) {
  return await jsonApiRequest("GET", `pim/data-quality/outliers?attributeDefinitionId=${attributeDefinitionId}&limit=${limit}`);
}

// ==================== BULK OPERATIONS ====================

export async function previewFindReplace(attributeDefinitionId, sourceValues, schemaId = null) {
  return await jsonApiRequest("POST", `pim/bulk-operations/find-replace/preview`, {
    attributeDefinitionId,
    sourceValues,
    schemaId,
  });
}

export async function executeFindReplace(schemaId, attributeDefinitionId, sourceValues, targetValue) {
  return await jsonApiRequest("POST", `pim/bulk-operations/find-replace`, {
    schemaId,
    attributeDefinitionId,
    sourceValues,
    targetValue,
  });
}

export async function previewFindReplaceRegex(schemaId, attributeDefinitionId, pattern, replacement) {
  return await jsonApiRequest("POST", `pim/bulk-operations/find-replace-regex/preview`, {
    schemaId,
    attributeDefinitionId,
    pattern,
    replacement,
  });
}

export async function executeFindReplaceRegex(schemaId, attributeDefinitionId, pattern, replacement) {
  return await jsonApiRequest("POST", `pim/bulk-operations/find-replace-regex`, {
    schemaId,
    attributeDefinitionId,
    pattern,
    replacement,
  });
}

export async function executeNormalize(schemaId, attributeDefinitionId) {
  return await jsonApiRequest("POST", `pim/bulk-operations/normalize`, {
    schemaId,
    attributeDefinitionId,
  });
}

export async function getBulkOperationStatus(jobId) {
  return await jsonApiRequest("GET", `pim/bulk-operations/${jobId}/status`);
}

export async function previewConsolidate(schemaId, sourceAttributeDefinitionIds, targetAttributeDefinitionId, conflictStrategy) {
  return await jsonApiRequest("POST", `pim/bulk-operations/consolidate/preview`, {
    schemaId,
    sourceAttributeDefinitionIds,
    targetAttributeDefinitionId,
    conflictStrategy,
  });
}

export async function executeConsolidate(schemaId, sourceAttributeDefinitionIds, targetAttributeDefinitionId, conflictStrategy) {
  return await jsonApiRequest("POST", `pim/bulk-operations/consolidate`, {
    schemaId,
    sourceAttributeDefinitionIds,
    targetAttributeDefinitionId,
    conflictStrategy,
  });
}

export async function convertAttributeUnit(schemaId, attributeDefinitionId, oldUnit, newUnit, conversionFactor) {
  return await jsonApiRequest("POST", `pim/bulk-operations/convert-unit`, {
    schemaId,
    attributeDefinitionId,
    // Explicit: the definition's unit may already be flipped by the time this runs.
    oldUnit,
    newUnit,
    conversionFactor,
  });
}

// ==================== PRODUCTS GRID ====================

export async function getProductsGrid(schemaId, page = 1, pageSize = 100, filters = {}) {
  const params = new URLSearchParams({ schemaId, page, pageSize });
  if (filters.productTypeId) params.append("productTypeId", filters.productTypeId);
  if (filters.categoryId) params.append("categoryId", filters.categoryId);
  if (filters.sourceImportId) params.append("sourceImportId", filters.sourceImportId);
  if (filters.status) params.append("status", filters.status);
  if (filters.search) params.append("search", filters.search);
  return await jsonApiRequest("GET", `pim/products/grid?${params.toString()}`);
}

export async function bulkUpdateProducts(updates) {
  return await jsonApiRequest("POST", `pim/products/bulk-update`, { updates });
}

export async function bulkSetProducts(productIds, attributeDefinitionId, value, schemaId = null) {
  return await jsonApiRequest("POST", `pim/products/bulk-set`, {
    productIds,
    attributeDefinitionId,
    value,
    schemaId,
  });
}

// ==================== CLASSIFICATION STANDARDS ====================

export async function getClassificationStandards() {
  return await jsonApiRequest("GET", `pim/classification-standards`);
}

export async function getClassificationStandardById(id) {
  return await jsonApiRequest("GET", `pim/classification-standards/${id}`);
}

export async function getClassificationClasses(standardId, params = {}) {
  let url = `pim/classification-standards/${standardId}/classes?`;
  if (params.parentCode) url += `parentCode=${encodeURIComponent(params.parentCode)}&`;
  if (params.q) url += `q=${encodeURIComponent(params.q)}&`;
  if (params.page) url += `page=${params.page}&`;
  if (params.pageSize) url += `pageSize=${params.pageSize}&`;
  return await jsonApiRequest("GET", url);
}

export async function getClassificationClassDetail(standardId, code) {
  return await jsonApiRequest("GET", `pim/classification-standards/${standardId}/classes/${encodeURIComponent(code)}`);
}

// ==================== GOOGLE SHOPPING CATEGORIES (CT-2) ====================

export async function getGoogleCategoryCoverage(schemaId) {
  return await jsonApiRequest("GET", `pim/schemas/${schemaId}/google-categories/coverage`);
}

export async function startGoogleCategoryMapping(schemaId, options = {}) {
  return await jsonApiRequest("POST", `pim/schemas/${schemaId}/google-categories/mapping-runs`, options);
}

export async function getGoogleCategoryMappingRun(schemaId, jobId) {
  return await jsonApiRequest("GET", `pim/schemas/${schemaId}/google-categories/mapping-runs/${jobId}`);
}

export async function getLatestGoogleCategoryMappingRun(schemaId) {
  return await jsonApiRequest("GET", `pim/schemas/${schemaId}/google-categories/mapping-runs/latest`);
}

export async function cancelGoogleCategoryMapping(schemaId, jobId) {
  return await jsonApiRequest("POST", `pim/schemas/${schemaId}/google-categories/mapping-runs/${jobId}/cancel`);
}

export async function getGoogleCategoryUnassigned(schemaId, limit = 200) {
  return await jsonApiRequest("GET", `pim/schemas/${schemaId}/google-categories/unassigned?limit=${limit}`);
}

export async function getGoogleCategoryAssignments(schemaId, { flag, limit = 200 } = {}) {
  let url = `pim/schemas/${schemaId}/google-categories/assignments?limit=${limit}`;
  if (flag) url += `&flag=${encodeURIComponent(flag)}`;
  return await jsonApiRequest("GET", url);
}

/**
 * Set (or, with an empty classCode, clear) one standard's classification on a product type.
 * Scoped to a single standard on purpose — correcting the Google category must not be able to
 * drop the ETIM class by omission.
 */
export async function setProductTypeClassification(productTypeId, standardCode, classCode, notes) {
  return await jsonApiRequest(
    "PUT",
    `pim/product-types/${productTypeId}/classifications/${standardCode}`,
    { classCode, notes },
  );
}

// ==================== STANDARD CLASS ADOPTION (ST-2) ====================

export async function getStandardClassSuggestions(productId) {
  return await jsonApiRequest("GET", `pim/products/${productId}/standard-class-suggestions`);
}

export async function adoptStandardClass(productId, { standardSchemaId, classCode, featureCodes }) {
  return await jsonApiRequest("POST", `pim/products/${productId}/adopt-standard-class`, {
    standardSchemaId,
    classCode,
    featureCodes,
  });
}

// ==================== CLASSIFICATION EXPORT ====================

export async function exportEtimXChange(body) {
  const response = await apiRequest("POST", `pim/classification-export/etim-xchange`, body);
  if (!response || !response.ok) throw new Error(await exportErrorText(response));
  const warnings = readExportWarnings(response);
  downloadBlobResponse(await response.blob(), "etim-xchange-export.json");
  return warnings;
}

/**
 * BMEcat export (2005 or 1.2). Returns the export's non-fatal warnings. A 400 here is the
 * exporter REFUSING rather than inventing data (e.g. an article whose price it will not state
 * as a number) — the reason is the response body and belongs in front of the user.
 */
export async function exportBmecat({ schemaId, productIds, version = "v2005" }) {
  const response = await apiRequest("POST", `pim/classification-export/bmecat`, {
    schemaId,
    productIds,
    version,
  });
  if (!response || !response.ok) throw new Error(await exportErrorText(response));
  const warnings = readExportWarnings(response);
  const label = version === "v12" ? "1.2" : "2005";
  downloadBlobResponse(await response.blob(), `bmecat-${label}-export.xml`);
  return warnings;
}

/** The server's refusal reason as plain text — BadRequest(string) arrives JSON-quoted. */
async function exportErrorText(response) {
  if (!response) return "Export failed";
  const text = await response.text();
  try {
    const parsed = JSON.parse(text);
    if (typeof parsed === "string") return parsed;
  } catch {
    /* plain text already */
  }
  return text || "Export failed";
}

function downloadBlobResponse(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ==================== IMPORT SCHEMA DEFINITION ====================

export async function setImportSchemaDefinition(importId, schemaDefinitionId) {
  const response = await jsonApiRequest("PUT", `imports/${importId}/schema-definition/${schemaDefinitionId}`);
  return response;
}

export async function clearImportSchemaDefinition(importId) {
  const response = await jsonApiRequest("DELETE", `imports/${importId}/schema-definition`);
  return response;
}

// ==================== CHANNELS ====================

export async function getPimChannels() {
  return await jsonApiRequest("GET", "pim/channels");
}

export async function getPimChannelById(id) {
  return await jsonApiRequest("GET", `pim/channels/${id}`);
}

export async function getPimChannelsBySchema(schemaId) {
  return await jsonApiRequest("GET", `pim/channels/by-schema/${schemaId}`);
}

export async function createPimChannel(channel) {
  return await jsonApiRequest("POST", "pim/channels", channel);
}

export async function updatePimChannel(id, channel) {
  return await jsonApiRequest("PUT", `pim/channels/${id}`, channel);
}

export async function deletePimChannel(id) {
  return await jsonApiRequest("DELETE", `pim/channels/${id}`);
}

export async function getChannelDashboard() {
  return await jsonApiRequest("GET", "pim/channels/dashboard");
}

export async function initializeChannelAttributeConfigs(channelId) {
  return await jsonApiRequest("POST", `pim/channels/${channelId}/initialize-attribute-configs`);
}

export async function getChannelAttributes(channelId) {
  return await jsonApiRequest("GET", `pim/channels/${channelId}/attributes`);
}
